import React, { useState, useEffect, useRef } from 'react';
import { getOffers, createOffer, deleteOffer } from '../../services/api';
import { format } from 'date-fns';

export default function OffersPage() {
  const [tab, setTab] = useState('listed'); // listed | add
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef();

  // Form state
  const [form, setForm] = useState({ name: '', subtitle: '', description: '' });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const { data } = await getOffers();
      setOffers(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadOffers(); }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.subtitle || !form.description) {
      alert('Please fill all fields');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('subtitle', form.subtitle);
      fd.append('description', form.description);
      if (image) fd.append('image', image);
      await createOffer(fd);
      setForm({ name: '', subtitle: '', description: '' });
      setImage(null);
      setImagePreview(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
      loadOffers();
      setTab('listed');
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Delete this offer?')) return;
    try {
      await deleteOffer(offerId);
      setOffers(prev => prev.filter(o => o._id !== offerId));
    } catch (err) { console.error(err); }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Offers</h1>
          <p style={styles.pageSubtitle}>Create and manage promotional offers</p>
        </div>
      </div>

      {/* Sub tabs */}
      <div style={styles.tabRow}>
        <button
          onClick={() => setTab('listed')}
          style={{ ...styles.tab, ...(tab === 'listed' ? styles.tabActive : {}) }}
        >
          📋 Listed Offers
          {offers.length > 0 && <span style={styles.tabBadge}>{offers.length}</span>}
        </button>
        <button
          onClick={() => setTab('add')}
          style={{ ...styles.tab, ...(tab === 'add' ? styles.tabActive : {}) }}
        >
          ✨ Add New
        </button>
      </div>

      {/* ADD NEW TAB */}
      {tab === 'add' && (
        <div style={styles.addForm}>
          <div style={styles.formGrid}>
            {/* Left: fields */}
            <div style={styles.formLeft}>
              <div style={styles.field}>
                <label style={styles.label}>OFFER NAME *</label>
                <input
                  style={styles.input}
                  placeholder="e.g. Welcome Bonus 100%"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>SUBTITLE *</label>
                <input
                  style={styles.input}
                  placeholder="e.g. For new players only"
                  value={form.subtitle}
                  onChange={e => setForm(p => ({ ...p, subtitle: e.target.value }))}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>DESCRIPTION *</label>
                <textarea
                  style={{ ...styles.input, height: '120px', resize: 'vertical' }}
                  placeholder="Describe the offer in detail..."
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                />
              </div>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ ...styles.submitBtn, ...(submitting ? styles.submitBtnDisabled : {}) }}
              >
                {submitting ? 'Creating...' : success ? '✅ Created!' : '🚀 Publish Offer'}
              </button>
            </div>

            {/* Right: image upload */}
            <div style={styles.formRight}>
              <label style={styles.label}>OFFER IMAGE</label>
              <div
                style={styles.imageUpload}
                onClick={() => fileRef.current?.click()}
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="preview" style={styles.imagePreview} />
                ) : (
                  <div style={styles.uploadPlaceholder}>
                    <span style={styles.uploadIcon}>🖼️</span>
                    <span style={styles.uploadText}>Click to upload</span>
                    <span style={styles.uploadHint}>PNG, JPG, JPEG</span>
                  </div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpg,image/jpeg"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
              {imagePreview && (
                <button
                  style={styles.clearImg}
                  onClick={() => { setImage(null); setImagePreview(null); }}
                >
                  ✕ Remove image
                </button>
              )}

              {/* Live preview card */}
              {form.name && (
                <div style={styles.previewCard}>
                  <div style={styles.previewLabel}>LIVE PREVIEW</div>
                  {imagePreview && (
                    <img src={imagePreview} alt="" style={styles.previewCardImg} />
                  )}
                  <div style={styles.previewCardBody}>
                    <div style={styles.previewCardTitle}>{form.name}</div>
                    <div style={styles.previewCardSub}>{form.subtitle}</div>
                    <div style={styles.previewCardDesc}>{form.description}</div>
                    <div style={styles.previewCardBtn}>CLAIM OFFER →</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* LISTED OFFERS TAB */}
      {tab === 'listed' && (
        <div>
          {loading ? (
            <div style={styles.loading}>Loading offers...</div>
          ) : offers.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎁</div>
              <div>No offers yet. Create one!</div>
              <button style={styles.createFirstBtn} onClick={() => setTab('add')}>
                + Create First Offer
              </button>
            </div>
          ) : (
            <div style={styles.offersGrid}>
              {offers.map(offer => (
                <div key={offer._id} style={styles.offerCard}>
                  {offer.imageUrl && (
                    <img src={offer.imageUrl} alt={offer.name} style={styles.offerImg} />
                  )}
                  {!offer.imageUrl && (
                    <div style={styles.offerImgPlaceholder}>🎁</div>
                  )}
                  <div style={styles.offerBody}>
                    <div style={styles.offerName}>{offer.name}</div>
                    <div style={styles.offerSub}>{offer.subtitle}</div>
                    <div style={styles.offerDesc}>{offer.description}</div>
                    <div style={styles.offerFooter}>
                      <span style={styles.offerDate}>
                        {offer.createdAt ? format(new Date(offer.createdAt), 'dd MMM yyyy') : ''}
                      </span>
                      <button
                        onClick={() => handleDelete(offer._id)}
                        style={styles.deleteBtn}
                      >
                        🗑 Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    flex: 1, padding: '28px', overflowY: 'auto',
    fontFamily: "'Exo 2', sans-serif", background: '#080c14',
  },
  pageHeader: { marginBottom: '24px' },
  pageTitle: {
    margin: 0, fontSize: '24px', fontWeight: '700',
    color: '#e2e8f0', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '1px',
  },
  pageSubtitle: { margin: '4px 0 0', color: 'rgba(255,255,255,0.3)', fontSize: '13px' },
  tabRow: { display: 'flex', gap: '8px', marginBottom: '24px' },
  tab: {
    padding: '11px 22px', borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'transparent', color: 'rgba(255,255,255,0.35)',
    cursor: 'pointer', fontSize: '14px',
    fontFamily: "'Exo 2', sans-serif", fontWeight: '600',
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  tabActive: {
    background: 'rgba(255,179,0,0.08)',
    border: '1px solid rgba(255,179,0,0.25)', color: '#FFB300',
  },
  tabBadge: {
    background: 'rgba(255,179,0,0.2)', borderRadius: '10px',
    padding: '1px 8px', fontSize: '11px', color: '#FFB300',
  },
  addForm: {
    background: '#0d1420', borderRadius: '16px',
    border: '1px solid rgba(255,179,0,0.08)', padding: '28px',
  },
  formGrid: { display: 'flex', gap: '28px', flexWrap: 'wrap' },
  formLeft: { flex: 1, minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '0' },
  formRight: { width: '280px', minWidth: '240px' },
  field: { marginBottom: '18px' },
  label: {
    display: 'block', marginBottom: '8px',
    color: 'rgba(255,179,0,0.7)', fontSize: '11px',
    fontWeight: '700', letterSpacing: '2px',
  },
  input: {
    width: '100%', background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,179,0,0.15)',
    borderRadius: '10px', padding: '12px 14px',
    color: '#fff', fontSize: '14px',
    fontFamily: "'Exo 2', sans-serif", outline: 'none',
    boxSizing: 'border-box',
  },
  submitBtn: {
    background: '#FFB300', border: 'none', borderRadius: '12px',
    padding: '14px 28px', color: '#080c14', fontSize: '15px',
    fontWeight: '900', fontFamily: "'Rajdhani', sans-serif",
    letterSpacing: '2px', cursor: 'pointer', marginTop: '8px',
    width: '100%',
  },
  submitBtnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  imageUpload: {
    width: '100%', height: '180px',
    border: '2px dashed rgba(255,179,0,0.2)',
    borderRadius: '14px', cursor: 'pointer',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', overflow: 'hidden',
    background: 'rgba(255,179,0,0.02)',
    marginBottom: '10px', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  uploadPlaceholder: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '6px',
  },
  uploadIcon: { fontSize: '36px' },
  uploadText: { color: 'rgba(255,179,0,0.6)', fontSize: '13px', fontWeight: '600' },
  uploadHint: { color: 'rgba(255,255,255,0.2)', fontSize: '11px' },
  imagePreview: { width: '100%', height: '100%', objectFit: 'cover' },
  clearImg: {
    background: 'none', border: 'none',
    color: 'rgba(255,100,100,0.6)', cursor: 'pointer',
    fontSize: '12px', padding: '0', marginBottom: '16px',
  },
  previewCard: {
    marginTop: '16px', borderRadius: '14px',
    overflow: 'hidden', border: '1px solid rgba(255,179,0,0.15)',
    background: 'linear-gradient(135deg, #0d1e2c, #091520)',
  },
  previewLabel: {
    fontSize: '9px', letterSpacing: '3px',
    color: 'rgba(255,179,0,0.4)', padding: '8px 14px 0',
    fontWeight: '700',
  },
  previewCardImg: { width: '100%', maxHeight: '100px', objectFit: 'cover' },
  previewCardBody: { padding: '12px 14px' },
  previewCardTitle: {
    color: '#fff', fontSize: '15px', fontWeight: '700',
    fontFamily: "'Rajdhani', sans-serif", marginBottom: '3px',
  },
  previewCardSub: {
    color: '#FFB300', fontSize: '11px', marginBottom: '6px',
  },
  previewCardDesc: {
    color: 'rgba(255,255,255,0.4)', fontSize: '11px',
    lineHeight: '1.5', marginBottom: '10px',
  },
  previewCardBtn: {
    background: 'linear-gradient(90deg, #FFB300, #00aaff)',
    color: '#080c14', borderRadius: '8px',
    padding: '7px 14px', fontSize: '11px',
    fontWeight: '900', display: 'inline-block',
    letterSpacing: '1px',
  },
  loading: {
    textAlign: 'center', color: 'rgba(255,255,255,0.3)',
    padding: '60px', fontSize: '15px',
  },
  empty: {
    textAlign: 'center', color: 'rgba(255,255,255,0.25)',
    padding: '60px 20px', fontSize: '15px',
  },
  createFirstBtn: {
    marginTop: '16px', background: 'rgba(255,179,0,0.08)',
    border: '1px solid rgba(255,179,0,0.2)', borderRadius: '10px',
    color: '#FFB300', padding: '12px 24px', cursor: 'pointer',
    fontSize: '14px', fontFamily: "'Exo 2', sans-serif", fontWeight: '600',
  },
  offersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '18px',
  },
  offerCard: {
    background: '#0d1420', borderRadius: '16px',
    border: '1px solid rgba(255,179,0,0.1)', overflow: 'hidden',
    transition: 'border-color 0.2s',
  },
  offerImg: { width: '100%', height: '150px', objectFit: 'cover' },
  offerImgPlaceholder: {
    width: '100%', height: '100px',
    background: 'rgba(255,179,0,0.04)',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '40px',
  },
  offerBody: { padding: '16px' },
  offerName: {
    fontSize: '17px', fontWeight: '700',
    color: '#e2e8f0', fontFamily: "'Rajdhani', sans-serif",
    marginBottom: '4px',
  },
  offerSub: { color: '#FFB300', fontSize: '12px', marginBottom: '8px' },
  offerDesc: {
    color: 'rgba(255,255,255,0.35)', fontSize: '12px',
    lineHeight: '1.5', marginBottom: '14px',
  },
  offerFooter: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.05)',
    paddingTop: '12px',
  },
  offerDate: { color: 'rgba(255,255,255,0.25)', fontSize: '11px' },
  deleteBtn: {
    background: 'rgba(255,50,50,0.1)',
    border: '1px solid rgba(255,50,50,0.25)',
    borderRadius: '8px', color: '#ff6b6b',
    padding: '5px 12px', cursor: 'pointer',
    fontSize: '12px', fontFamily: "'Exo 2', sans-serif",
  },
};